import { buildingDef, type BuildingTypeId } from '../buildings/definitions';
import type { Command, CommandResult } from '../commands';
import type { GridPoint } from '../../core/types';
import type { PlayerId } from '../ids';
import type { Simulation } from '../Simulation';
import type { UnitTypeId } from '../units/unitDefs';

/**
 * Computer-controlled colonies (D115). Lives INSIDE the simulation: it reads
 * state and acts only through validated commands (the same `apply` a human's
 * orders go through), on fixed ticks and in a fixed order — so it is
 * deterministic, replayable, and can never cheat on costs. Difficulty only
 * changes how fast it thinks, how early it attacks and how big its army is.
 */
/** `survival`: a defence-first player used to playtest the campaign (D116). */
export type AIDifficulty = 'easy' | 'normal' | 'hard' | 'survival';

export interface AITuning {
  /** Ticks between decisions (10 ticks = 1 s). */
  readonly thinkTicks: number;
  /** No attack before this many seconds. */
  readonly graceSeconds: number;
  /** Army size that triggers the first attack; each later attack needs `attackGrowth` more. */
  readonly attackAt: number;
  readonly attackGrowth: number;
  /** Seconds between attacks. */
  readonly attackEvery: number;
  /** Watchtowers it aims for. */
  readonly towers: number;
  /** Builds Generators and Gun Towers. */
  readonly gunTowers: boolean;
  /** Heavies per Ranger (0.5 = one Heavy for every two Rangers). */
  readonly heavyRatio: number;
  /** Barracks and towers before growth (survival play). */
  readonly defenceFirst?: boolean;
}

export const AI_TUNING: Readonly<Record<AIDifficulty, AITuning>> = {
  easy: { thinkTicks: 40, graceSeconds: 600, attackAt: 10, attackGrowth: 2, attackEvery: 240, towers: 1, gunTowers: false, heavyRatio: 0.25 },
  normal: { thinkTicks: 25, graceSeconds: 420, attackAt: 8, attackGrowth: 3, attackEvery: 180, towers: 2, gunTowers: false, heavyRatio: 0.4 },
  hard: { thinkTicks: 15, graceSeconds: 270, attackAt: 7, attackGrowth: 4, attackEvery: 130, towers: 3, gunTowers: true, heavyRatio: 0.5 },
  survival: { thinkTicks: 10, graceSeconds: 1e9, attackAt: 999, attackGrowth: 0, attackEvery: 1e9, towers: 6, gunTowers: true, heavyRatio: 0.5, defenceFirst: true },
};

/** How close an enemy must be to the Command Center to call the army home (tiles). */
const DEFEND_RADIUS = 11;

interface Brain {
  readonly id: PlayerId;
  readonly tuning: AITuning;
  readonly offset: number;
  attacks: number;
  nextAttackAt: number;
  attacking: boolean;
  /** Building type → no site found; retry after this time (s). */
  readonly noSite: Map<BuildingTypeId, number>;
}

export class ColonyAI {
  private readonly brains: Brain[];

  constructor(
    private readonly sim: Simulation,
    players: readonly { readonly id: PlayerId; readonly difficulty: AIDifficulty }[],
  ) {
    this.brains = players.map((p, i) => ({
      id: p.id,
      tuning: AI_TUNING[p.difficulty],
      offset: (i * 7) % AI_TUNING[p.difficulty].thinkTicks,
      attacks: 0,
      nextAttackAt: AI_TUNING[p.difficulty].graceSeconds,
      attacking: false,
      noSite: new Map(),
    }));
  }

  get players(): readonly PlayerId[] {
    return this.brains.map((b) => b.id);
  }

  /** Called once per tick by the simulation, after everything else moved. */
  step(tick: number): void {
    for (const b of this.brains) {
      if ((tick + b.offset) % b.tuning.thinkTicks !== 0) continue;
      if (this.sim.match.result || this.sim.match.statusOf(b.id) !== 'playing') continue;
      this.think(b, tick / 10);
    }
  }

  snapshot(): { id: string; attacks: number; nextAttackAt: number; attacking: boolean }[] {
    return this.brains.map((b) => ({ id: b.id, attacks: b.attacks, nextAttackAt: b.nextAttackAt, attacking: b.attacking }));
  }

  // ------------------------------------------------------------------

  private act(cmd: Command): CommandResult {
    return this.sim.apply(cmd);
  }

  private think(b: Brain, seconds: number): void {
    const sim = this.sim;
    const own = sim.buildings.ownedBy(b.id);
    const cc = own.find((x) => x.type === 'command_center');
    if (!cc) return;
    const count = (t: BuildingTypeId) => own.filter((x) => x.type === t).length;
    const building = own.some((x) => x.state !== 'complete');
    const pop = sim.colony.population(b.id);
    const cap = sim.colony.capacity(b.id);
    const army = sim.units.ownedBy(b.id).filter((u) => u.type !== 'engineer');

    // 1. Economy and defences, one construction at a time (like a careful human).
    if (!building) {
      for (const want of this.wanted(b, count, pop, cap, seconds)) {
        if (want === 'wait') break;
        if ((b.noSite.get(want) ?? -1) > seconds) continue;
        if (this.place(b.id, want, cc.origin)) break;
        // Nowhere to put it (e.g. no forest left for a Sawmill): try again in a minute, build the next thing meanwhile.
        b.noSite.set(want, seconds + 60);
      }
    }

    // 2. Training: keep the Barracks busy when there is spare food and idle survivors.
    const barracks = own.find((x) => x.type === 'barracks' && x.state === 'complete');
    // Recruits come out of the population (workers included), so keep a working core of survivors.
    const keep = b.tuning.defenceFirst ? 6 : 8;
    if (barracks && sim.units.queue(barracks.id).length < 2 && pop > keep && !sim.economy.hasShortage(b.id)) {
      const heavies = army.filter((u) => u.type === 'heavy').length;
      const rangers = army.filter((u) => u.type === 'ranger').length;
      const unit: UnitTypeId = heavies < rangers * b.tuning.heavyRatio ? 'heavy' : 'ranger';
      this.act({ type: 'trainUnit', playerId: b.id, buildingId: barracks.id, unit });
    }

    // 3. Defence: anything hostile near the Command Center calls the whole army home.
    const centre = { x: cc.origin.x + 1, y: cc.origin.y + 1 };
    const threat = sim.units.enemiesNear(b.id, centre.x, centre.y, DEFEND_RADIUS)[0];
    if (threat && army.length) {
      b.attacking = false;
      const idle = army.filter((u) => u.order.kind !== 'attack' && u.order.kind !== 'attackMove').map((u) => u.id);
      if (idle.length) this.act({ type: 'attackMove', playerId: b.id, unitIds: idle.slice(0, 40), target: { x: Math.round(threat.x), y: Math.round(threat.y) } });
      return;
    }

    // 3b. Campaign objectives (D124), for the playtest bot: scouts walk to survivor camps; a big enough army burns the nearest nest.
    const objectives = sim.objectives;
    if (objectives && b.tuning.defenceFirst) {
      const camps = sim.buildings.all().filter((x) => x.type === 'survivor_camp');
      const idleArmy = army.filter((u) => u.order.kind === 'idle');
      if (camps.length && idleArmy.length >= 6) {
        // A squad fights its way to the nearest camp; standing there afterwards brings the survivors in.
        const camp = camps.slice().sort((p, q) => Math.abs(p.origin.x - centre.x) + Math.abs(p.origin.y - centre.y) - (Math.abs(q.origin.x - centre.x) + Math.abs(q.origin.y - centre.y)))[0]!;
        this.act({ type: 'attackMove', playerId: b.id, unitIds: idleArmy.slice(0, 8).map((u) => u.id), target: { x: camp.origin.x + 1, y: camp.origin.y + 3 } });
      }
      const nests = sim.buildings.all().filter((x) => x.type === 'infected_nest');
      if (nests.length && army.length >= 7 && army.filter((u) => u.order.kind === 'idle').length >= 6) {
        const nest = nests.slice().sort((p, q) => Math.abs(p.origin.x - centre.x) + Math.abs(p.origin.y - centre.y) - (Math.abs(q.origin.x - centre.x) + Math.abs(q.origin.y - centre.y)))[0]!;
        // Attack-move: fight the guards on the way in, then the nest itself (closest enemy building) falls.
        this.act({ type: 'attackMove', playerId: b.id, unitIds: army.map((u) => u.id).slice(0, 40), target: { x: nest.origin.x + 1, y: nest.origin.y + 1 } });
      }
      return;
    }

    // 4. Attack: once the army is big enough and the grace period is over, march on the nearest rival.
    if (b.attacking && army.every((u) => u.order.kind === 'idle')) b.attacking = false;
    const needed = b.tuning.attackAt + b.tuning.attackGrowth * b.attacks;
    if (!b.attacking && seconds >= b.nextAttackAt && army.length >= needed) {
      const target = this.nearestRivalCentre(b.id, centre);
      if (target) {
        const ids = army.map((u) => u.id).slice(0, 40);
        const r = this.act({ type: 'attackMove', playerId: b.id, unitIds: ids, target });
        if (r.ok) {
          b.attacking = true;
          b.attacks++;
          b.nextAttackAt = seconds + b.tuning.attackEvery;
        }
      }
    } else if (!b.attacking) {
      // Between attacks the army gathers just outside the base, facing the map centre.
      const rally = this.rallyPoint(centre);
      const strays = army.filter((u) => u.order.kind === 'idle' && Math.abs(u.x - rally.x) + Math.abs(u.y - rally.y) > 6).map((u) => u.id);
      if (strays.length) this.act({ type: 'moveUnits', playerId: b.id, unitIds: strays.slice(0, 40), target: rally });
    }
  }

  /**
   * The build order as rules: food first, then wood / scrap, housing, an army,
   * storage, towers. Needed buildings in priority order; 'wait' where the next
   * one is not affordable yet (save up rather than skip ahead).
   */
  private wanted(b: Brain, count: (t: BuildingTypeId) => number, pop: number, cap: number, seconds: number): (BuildingTypeId | 'wait')[] {
    const sim = this.sim;
    const ledger = sim.economy.ledger(b.id);
    const farms = count('farm');
    const housingFull = pop >= cap - 1;
    const storeFull = ledger.get('wood') >= ledger.capacity('wood') * 0.9 || ledger.get('scrap') >= ledger.capacity('scrap') * 0.9;
    const hungry = ledger.get('food') < 25 && sim.economy.hasShortage(b.id);
    const order: [boolean, BuildingTypeId][] = [
      [farms < 1 || (hungry && farms < 10), 'farm'],
      [count('sawmill') < 1, 'sawmill'],
      [count('scrap_yard') < 1, 'scrap_yard'],
      [!!b.tuning.defenceFirst && count('barracks') < 1, 'barracks'],
      [!!b.tuning.defenceFirst && count('watchtower') < 2, 'watchtower'],
      [housingFull && count('survivor_housing') < 6, 'survivor_housing'],
      [farms < 2, 'farm'],
      [count('barracks') < 1, 'barracks'],
      [storeFull && count('warehouse') < 3, 'warehouse'],
      [count('watchtower') < Math.min(b.tuning.towers, 1 + Math.floor(seconds / 240)), 'watchtower'],
      [count('sawmill') < 2 && seconds > 240, 'sawmill'],
      [count('scrap_yard') < 2 && seconds > 300, 'scrap_yard'],
      [farms < 2 + Math.floor(pop / 8), 'farm'],
      [b.tuning.gunTowers && count('generator') < 1 && seconds > 360, 'generator'],
      [b.tuning.gunTowers && count('gun_tower') < 2 * count('generator'), 'gun_tower'],
      [count('survivor_housing') < 2 + Math.floor(seconds / 300), 'survivor_housing'],
    ];
    const out: (BuildingTypeId | 'wait')[] = [];
    for (const [need, type] of order) {
      if (!need || !sim.isUnlocked(b.id, type) || out.includes(type)) continue;
      if ((b.noSite.get(type) ?? -1) > seconds) continue;
      if (!ledger.canAfford(buildingDef(type).cost).ok) {
        out.push('wait');
        break;
      }
      out.push(type);
    }
    return out;
  }

  /**
   * First valid site on a ring search around the Command Center that keeps a
   * one-tile lane free around the footprint (so the colony never walls itself in).
   */
  private place(p: PlayerId, type: BuildingTypeId, ccOrigin: GridPoint): boolean {
    const sim = this.sim;
    const fp = buildingDef(type).footprint;
    const cx = ccOrigin.x + 1;
    const cy = ccOrigin.y + 1;
    for (let r = 3; r <= 14; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const origin = { x: cx + dx, y: cy + dy };
          if (!this.laneFree(origin, fp)) continue;
          const cmd: Command = { type: 'placeBuilding', playerId: p, building: type, origin };
          if (!sim.validate(cmd).ok) continue;
          return this.act(cmd).ok;
        }
      }
    }
    return false;
  }

  private laneFree(origin: GridPoint, fp: { width: number; height: number }): boolean {
    for (let y = origin.y - 1; y <= origin.y + fp.height; y++) {
      for (let x = origin.x - 1; x <= origin.x + fp.width; x++) {
        if (this.sim.buildings.at(x, y)) return false;
      }
    }
    return true;
  }

  private nearestRivalCentre(p: PlayerId, from: GridPoint): GridPoint | null {
    let best: GridPoint | null = null;
    let bestD = Infinity;
    for (const other of this.sim.allPlayers()) {
      if (other.id === p || this.sim.match.statusOf(other.id) !== 'playing') continue;
      if ((this.sim.teams.get(other.id) ?? -1) === (this.sim.teams.get(p) ?? -2)) continue;
      const cc = this.sim.buildings.ownedBy(other.id).find((x) => x.type === 'command_center');
      if (!cc) continue;
      const d = Math.abs(cc.origin.x - from.x) + Math.abs(cc.origin.y - from.y);
      if (d < bestD || (d === bestD && best && other.id < p)) {
        bestD = d;
        best = { x: cc.origin.x + 1, y: cc.origin.y + 1 };
      }
    }
    return best;
  }

  private rallyPoint(centre: GridPoint): GridPoint {
    const mid = { x: Math.floor(this.sim.world.width / 2), y: Math.floor(this.sim.world.height / 2) };
    const dx = Math.sign(mid.x - centre.x);
    const dy = Math.sign(mid.y - centre.y);
    const x = Math.max(0, Math.min(this.sim.world.width - 1, centre.x + dx * 5));
    const y = Math.max(0, Math.min(this.sim.world.height - 1, centre.y + dy * 5));
    return { x, y };
  }
}
